import React, { useState, useRef } from "react";
import { Checkbox, Input, Select, Radio, Button } from "antd";
import { PaperClipOutlined } from '@ant-design/icons';
import "./styles.css";

const { Option } = Select;
const { TextArea } = Input;

const NotesAndRecommendations = ({
  notes,
  setNotes,
  prescription,
  setPrescription,
  prescriptionNotRequired,
  setPrescriptionNotRequired,
  inCall = false,
  recommendationFiles = [],
  setRecommendationFiles = () => {},
  // New observation props
  selfHarmSymptoms = "LOW",
  setSelfHarmSymptoms = () => {},
  depressionSymptoms = "LOW", 
  setDepressionSymptoms = () => {},
  anxietySymptoms = "LOW",
  setAnxietySymptoms = () => {},
  angerIssues = "LOW",
  setAngerIssues = () => {},
  // New recommendation props
  assessments = "",
  setAssessments = () => {},
  exercises = "",
  setExercises = () => {},
  clinicalConsultation = false,
  setClinicalConsultation = () => {},
  medicalConsultation = false,
  setMedicalConsultation = () => {},
  // File upload props
  assessmentFiles = [],
  setAssessmentFiles = () => {},
  exerciseFiles = [],
  setExerciseFiles = () => {},
}) => {

  // Recommendations input method states
  const [recommendationsInputMethod, setRecommendationsInputMethod] = useState("type"); // "type" or "file"
  
  // Custom file name states
  const [customAssessmentFileName, setCustomAssessmentFileName] = useState("");
  const [customExerciseFileName, setCustomExerciseFileName] = useState("");
  const [customRecommendationFileName, setCustomRecommendationFileName] = useState("");

  const symptomOptions = [
    { label: "None", value: "NONE" },
    { label: "Low", value: "LOW" },
    { label: "Moderate", value: "MODERATE" },
    { label: "High", value: "HIGH" },
  ];

  // File input refs to avoid Upload modal interference
  const assessmentFileInputRef = useRef(null);
  const exerciseFileInputRef = useRef(null);
  const recommendationFileInputRef = useRef(null);

  const handleFileSelect = (type) => {
    // Don't allow file selection when prescription is not required
    if (prescriptionNotRequired) return;
    
    if (type === 'assessment') {
      assessmentFileInputRef.current?.click();
    } else if (type === 'exercise') {
      exerciseFileInputRef.current?.click();
    } else if (type === 'recommendation') {
      recommendationFileInputRef.current?.click();
    }
  };

  const handleFileChange = (e, setFiles) => {
    const files = Array.from(e.target.files);
    setFiles(files);
    // Clear the input value to allow selecting the same file again
    e.target.value = '';
  };

  const removeFile = (index, files, setFiles, inputRef = null) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    // Clear the input value when removing files
    if (inputRef && inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={`observations-recommendations-container ${inCall ? 'in-call' : ''}`}>
      {/* Observations Section */}
      <div className="section-container">
        <h2 className="section-title">Observations</h2>
        <div className="separator" />

        {/* First Row - Self Harm & Depression */}
        <div className="observation-row">
          <div className="observation-field">
            <label className="field-label">Self Harm Symptoms</label>
            <Select
              placeholder="Select"
              value={selfHarmSymptoms}
              onChange={setSelfHarmSymptoms}
              className="custom-select"
            >
              {symptomOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>
          <div className="observation-field">
            <label className="field-label">Depression Symptoms</label>
            <Select
              placeholder="Select"
              value={depressionSymptoms}
              onChange={setDepressionSymptoms}
              className="custom-select"
            >
              {symptomOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>
        </div>

        <div className="separator" />

        {/* Second Row - Anxiety & Anger */}
        <div className="observation-row">
          <div className="observation-field">
            <label className="field-label">Anxiety Symptoms</label>
            <Select
              placeholder="Select"
              value={anxietySymptoms}
              onChange={setAnxietySymptoms}
              className="custom-select"
            >
              {symptomOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>
          <div className="observation-field">
            <label className="field-label">Anger Issues</label>
            <Select
              placeholder="Select"
              value={angerIssues}
              onChange={setAngerIssues}
              className="custom-select"
            >
              {symptomOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>
        </div>

        <div className="separator" />

        {/* Add Note */}
        <div className="note-field">
          <label className="field-label">Add Note</label>
          <TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Type Notes"
            className="custom-textarea"
            rows={2}
          />
        </div>
      </div>

      <div className="section-separator" />

      {/* Recommendations Section */}
      <div className="section-container">
        <div className="recommendations-header">
          <h2 className="section-title">Recommendations</h2>
          <div className="not-required-container">
            <Checkbox
              checked={prescriptionNotRequired}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setPrescriptionNotRequired(isChecked);

                // Clear all recommendation data when marking as "not required"
                if (isChecked) {
                  setAssessmentFiles([]);
                  setExerciseFiles([]);
                  setRecommendationFiles([]);
                  setAssessments("");
                  setExercises("");
                  setPrescription("");
                  setClinicalConsultation(false);
                  setMedicalConsultation(false);

                  // Clear file input refs
                  if (assessmentFileInputRef.current) {
                    assessmentFileInputRef.current.value = '';
                  }
                  if (exerciseFileInputRef.current) {
                    exerciseFileInputRef.current.value = '';
                  }
                  if (recommendationFileInputRef.current) {
                    recommendationFileInputRef.current.value = '';
                  }
                }
              }}
            >
              <span className="not-required-text">Not required</span>
            </Checkbox>
          </div>
        </div>

        <div className="separator" />

        {/* Assessments */}
        <div className="ass-exe">
        <div className="recommendation-field">
          <label className="field-label">Assessments</label>
          <div className="input-with-attachment">
            <div className="input-row">
              <Input
                value={assessments}
                onChange={(e) => setAssessments(e.target.value)}
                placeholder="Type here..."
                className="custom-input"
                disabled={prescriptionNotRequired}
              />
              <input
                type="file"
                ref={assessmentFileInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, setAssessmentFiles)}
                disabled={prescriptionNotRequired}
              />
              <Button 
                className="attachment-button" 
                icon={<PaperClipOutlined />}
                onClick={() => handleFileSelect('assessment')}
                disabled={prescriptionNotRequired}
              />
            </div>
            {assessmentFiles.length > 0 && (
              <div className="file-list">
                {assessmentFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <button 
                      className="file-remove" 
                      onClick={() => removeFile(index, assessmentFiles, setAssessmentFiles, assessmentFileInputRef)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exercises */}
      <div className="ass-exe">
      <div className="recommendation-field">
        <label className="field-label">Exercises</label>
        <div className="input-with-attachment">
          <div className="input-row">
            <Input
              value={exercises}
              onChange={(e) => setExercises(e.target.value)}
              placeholder="Type here..."
              className="custom-input"
              disabled={prescriptionNotRequired}
            />
            <input
              type="file"
              ref={exerciseFileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange(e, setExerciseFiles)}
              disabled={prescriptionNotRequired}
            />
            <Button 
              className="attachment-button" 
              icon={<PaperClipOutlined />}
              onClick={() => handleFileSelect('exercise')}
              disabled={prescriptionNotRequired}
            />
          </div>
          {exerciseFiles.length > 0 && (
            <div className="file-list">
              {exerciseFiles.map((file, index) => (
                <div key={index} className="file-item">
                  <span className="file-name">{file.name}</span>
                  <button 
                    className="file-remove" 
                    onClick={() => removeFile(index, exerciseFiles, setExerciseFiles, exerciseFileInputRef)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>

      <div className="separator" />

      {/* Consultation Options */}
      <div className="consultation-row">
        <div className="consultation-field">
          <label className="field-label">Clinical Consultation</label>
          <Radio.Group
            value={clinicalConsultation}
            onChange={(e) => setClinicalConsultation(e.target.value === true || e.target.value === "true")}
            className="radio-group"
            disabled={prescriptionNotRequired}
          >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </div>
        <div className="consultation-field">
          <label className="field-label">Medical Consultation</label>
          <Radio.Group
            value={medicalConsultation}
            onChange={(e) => setMedicalConsultation(e.target.value === true || e.target.value === "true")}
            className="radio-group"
            disabled={prescriptionNotRequired}
          >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </div>
      </div>

      <div className="separator" />

      {/* Add Recommendation */}
      <div className="note-field">
        <label className="field-label">How would you like to add your recommendations?</label>
        <Radio.Group
          value={recommendationsInputMethod}
          onChange={(e) => setRecommendationsInputMethod(e.target.value)}
          className="radio-group"
          style={{ marginBottom: "12px" }}
          disabled={prescriptionNotRequired}
        >
          <Radio value="file">Attach File</Radio>
          <Radio value="type">Type Recommendations</Radio>
        </Radio.Group>

        {recommendationsInputMethod === "type" ? (
          <TextArea
            value={prescription}
            onChange={(e) => setPrescription(e.target.value)}
            placeholder="Type Recommendation"
            className="custom-textarea"
            rows={2}
            disabled={prescriptionNotRequired}
          />
        ) : (
          <div className="input-with-attachment">
            <div className="input-row">
              <Input
                placeholder="File Name..."
                className="custom-input"
                value={customRecommendationFileName || (recommendationFiles.length > 0 ? recommendationFiles[0].name : "")}
                onChange={(e) => setCustomRecommendationFileName(e.target.value)}
                disabled={prescriptionNotRequired}
              />
              <input
                type="file"
                ref={recommendationFileInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, setRecommendationFiles)}
                disabled={prescriptionNotRequired}
              />
              <Button 
                className="attachment-button" 
                icon={<PaperClipOutlined />}
                onClick={() => handleFileSelect('recommendation')}
                disabled={prescriptionNotRequired}
              />
            </div>
            {recommendationFiles.length > 0 && (
              <div className="file-list">
                {recommendationFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <button 
                      className="file-remove" 
                      onClick={() => removeFile(index, recommendationFiles, setRecommendationFiles, recommendationFileInputRef)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </div >
  );
};

export default NotesAndRecommendations;